import { ApiClient, FileShareClient } from "@anna-money/anna-web-lib/api";
import { type NavigateFunction } from "react-router-dom";
import { Memoize } from "typescript-memoize";
import { AsicAgentSignatureStore } from "services/asicAgentNomination/AsicAgentSignatureStore";
import { CompanyNameAnnexNOStore } from "services/companyNameForm/gb/CompanyNameAnnexNOStore";
import { CountryClient } from "services/country/countryClient";
import { CountryStore } from "services/country/countryStore";
import { DataStorage } from "services/storage/dataStorage";
import { AuthClient } from "./auth/authClient";
import { AuthSessionStorage } from "./auth/authSessionStorage";
import { AuthStore } from "./auth/authStore";
import { AuthenticationHandler } from "./auth/authenticationApiHandler";
import { Authenticator } from "./auth/authenticator";
import { Config } from "./config/config";
import { apiSerializer, apiSerializerNew } from "./helpers/serialization";
import { ABTestingService } from "./services/abTesting";
import { AddressClient } from "./services/address/addressClient";
import { AddressSearchClient } from "./services/addressSearch/addressSearchClient";
import { AddressSearchStore } from "./services/addressSearch/addressSearchStore";
import { AddressVerificationClient } from "./services/addressVerification/addressVerificationClient";
import { AddressVerificationStore } from "./services/addressVerification/addressVerificationStore";
import { AnalyticsManager } from "./services/analytics/analyticsManager";
import { BillingClient } from "./services/billing/billingClient";
import { RefundStore } from "./services/billing/refundStore";
import { BillingControllerStore } from "./services/billingController/billingControllerStore";
import { BusinessActivitiesClient } from "./services/businessActivities/businessActivitiesClient";
import { BusinessActivitiesStore } from "./services/businessActivities/businessActivitiesStore";
import { CardDetailsClient } from "./services/cardDetails/cardDetailsClient";
import { CardDetailsStore } from "./services/cardDetails/cardDetailsStore";
import { CompanyClient } from "./services/company/companyClient";
import { CompanyStore } from "./services/company/companyStore";
import { CompanyProductsClient } from "./services/companyProducts/companyProductsClient";
import { CompanyProductsStore } from "./services/companyProducts/companyProductsStore";
import { ComplianceClient } from "./services/compliance/complianceClient";
import { ComplianceStore } from "./services/compliance/complianceStore";
import { ComplianceCallClient, type IComplianceCallClient } from "./services/complianceCall/complianceCallClient";
import { ComplianceCallStore } from "./services/complianceCall/complianceCallStore";
import { ComplianceWaitStore } from "./services/complianceCall/complianceWaitStore";
import { ConfirmStepStore } from "./services/confirmStep/confirmStepStore";
import { ConsentClient } from "./services/consent/consentClient";
import { ConsentStore } from "./services/consent/consentStore";
import { DeepLinkClient } from "./services/deepLink/deepLinkClient";
import { DirectorAddressStore } from "./services/director/directorAddressStore";
import { DirectorClient } from "./services/director/directorClient";
import { DirectorStore } from "./services/director/directorStore";
import { DirectorIdentityWidgetStore } from "./services/directorIdentity/directorIdentityWidgetStore";
import { DirectorPoaStore } from "./services/directorIdentity/directorPoaStore";
import { DirectorPositionStore } from "./services/directorPosition/directorPositionStore";
import { FeatureStore } from "./services/feature/featureStore";
import { FormationExpressStore } from "./services/formationExpress/formationExpressStore";
import { IncorporationClient } from "./services/incorporation/incorporationClient";
import { IncorporationStore } from "./services/incorporation/incorporationStore";
import { IssuesClient } from "./services/issues/issuesClient";
import { IssuesStore } from "./services/issues/issuesStore";
import { LeadClient } from "./services/lead/leadClient";
import { NameCheckerClient } from "./services/nameChecker/nameCheckerClient";
import { NameCheckerStore } from "./services/nameChecker/nameCheckerStore";
import { NationalitiesClient } from "./services/nationalities/nationalitiesClient";
import { NationalitiesStore } from "./services/nationalities/nationalitiesStore";
import { PaymentMethodsStore } from "./services/paymentMethods/paymentMethodsStore";
import { ProfileStore } from "./services/profile/profileStore";
import { SentryHost } from "./services/sentry/sentryHost";
import { SetupIntentStore } from "./services/setupIntent/setupIntentStore";
import { CookieStorage } from "./services/storage/cookieStorage";
import { StripeFormStore } from "./services/stripeForm/stripeFormStore";
import { StripeIntegrationClient } from "./services/stripeIntegration/stripeIntegrationClient";
import { SumsubApplicationStore } from "./services/sumsub/sumsubApplicationStore";
import { SumsubClient } from "./services/sumsub/sumsubClient";
import type { UserClient } from "./services/user/userClient";
import { UserStore } from "./services/user/userStore";
import { type UpdateFormCallback } from "./types/form";

export class BaseServices {
  constructor(config: Config) {
    this.config = config;
  }

  readonly config: Config;

  @Memoize()
  get sentryHost(): SentryHost {
    return new SentryHost();
  }

  @Memoize()
  get dataStorage(): DataStorage {
    return new DataStorage();
  }

  @Memoize()
  get authClient(): AuthClient {
    return new AuthClient(this.config.annaAuthUrl);
  }

  @Memoize()
  get authenticator(): Authenticator {
    return new Authenticator(this.config, this.authClient, this.dataStorage, this.analyticsManager);
  }

  @Memoize()
  get authSessionStorage(): AuthSessionStorage {
    return new AuthSessionStorage(this.dataStorage);
  }

  @Memoize()
  get authStore(): AuthStore {
    return new AuthStore(this.authenticator, this.authSessionStorage);
  }

  @Memoize()
  get analyticsManager(): AnalyticsManager {
    return new AnalyticsManager(this.config.annaApiUrl, this.config.webticsTrackerUrl, this.config.appVersion);
  }

  @Memoize()
  get abTestingService(): ABTestingService {
    return new ABTestingService();
  }
}

export abstract class Services {
  protected constructor(
    protected readonly _baseServices: BaseServices,
    protected readonly _authenticationHandler: AuthenticationHandler,
    protected readonly _apiClient: ApiClient,
    protected readonly _userClient: UserClient,
    protected readonly _userStore: UserStore,
    protected readonly _profileStore: ProfileStore,
    protected readonly _featureStore: FeatureStore,
    protected readonly _navigate: NavigateFunction,
  ) {}

  @Memoize()
  get config(): Config {
    return this._baseServices.config;
  }

  @Memoize()
  private get authenticationHandler(): AuthenticationHandler {
    return this._authenticationHandler;
  }

  @Memoize()
  get apiClient(): ApiClient {
    return this._apiClient;
  }

  @Memoize()
  get analyticsManager(): AnalyticsManager {
    return this._baseServices.analyticsManager;
  }

  @Memoize()
  get navidate(): NavigateFunction {
    return this._navigate;
  }

  @Memoize()
  get profileStore(): ProfileStore {
    return this._profileStore;
  }

  @Memoize()
  get featureStore(): FeatureStore {
    return this._featureStore;
  }

  @Memoize()
  get userStore(): UserStore {
    return this._userStore;
  }

  @Memoize()
  get sentryHost(): SentryHost {
    return new SentryHost();
  }

  @Memoize()
  get authenticator(): Authenticator {
    return new Authenticator(
      this.config,
      new AuthClient(this.config.annaAuthUrl),
      this.dataStorage,
      this.analyticsManager,
    );
  }

  @Memoize()
  get authSessionStorage(): AuthSessionStorage {
    return new AuthSessionStorage(this.dataStorage);
  }

  @Memoize()
  get authStore(): AuthStore {
    return new AuthStore(this.authenticator, this.authSessionStorage);
  }

  @Memoize()
  get fileShareClient(): FileShareClient {
    const mediaApiClient = new ApiClient({
      apiUrl: this.config.annaMediaApiUrl,
      apiSerializerOld: apiSerializer,
      apiSerializerNew,
      handlers: [this.authenticationHandler],
    });

    return new FileShareClient(this.apiClient, mediaApiClient);
  }

  @Memoize()
  get dataStorage(): DataStorage {
    return new DataStorage();
  }

  @Memoize()
  get cookieStorage(): CookieStorage {
    return new CookieStorage();
  }

  @Memoize()
  get addressClient(): AddressClient {
    return new AddressClient(this.apiClient);
  }

  @Memoize()
  get addressSearchStore(): AddressSearchStore {
    return new AddressSearchStore(new AddressSearchClient(this.apiClient));
  }

  @Memoize()
  get addressVerificationStore(): AddressVerificationStore {
    return new AddressVerificationStore(
      this.config,
      this.companyStore,
      this.directorStore,
      this.directorAddressStore,
      new AddressVerificationClient(this.apiClient),
      this.featureStore,
    );
  }

  @Memoize()
  get businessActivitiesClient(): BusinessActivitiesClient {
    return new BusinessActivitiesClient(this.apiClient);
  }

  @Memoize()
  get businessActivitiesStore(): BusinessActivitiesStore {
    return new BusinessActivitiesStore(this.businessActivitiesClient);
  }

  @Memoize()
  get countryClient(): CountryClient {
    return new CountryClient(this.apiClient);
  }

  @Memoize()
  get countryStore(): CountryStore {
    return new CountryStore(this.countryClient);
  }

  @Memoize()
  get cardDetailsStore(): CardDetailsStore {
    return new CardDetailsStore(new CardDetailsClient(this.apiClient), this.analyticsManager);
  }

  @Memoize()
  get companyClient(): CompanyClient {
    return new CompanyClient(this.apiClient, this.authStore, this.analyticsManager);
  }

  @Memoize()
  get companyStore(): CompanyStore {
    return new CompanyStore(this.companyClient, this.featureStore);
  }

  @Memoize()
  get consentStore(): ConsentStore {
    return new ConsentStore(new ConsentClient(this.apiClient));
  }

  @Memoize()
  get issuesStore(): IssuesStore {
    return new IssuesStore(new IssuesClient(this.apiClient));
  }

  @Memoize()
  get companyProductsClient(): CompanyProductsClient {
    return new CompanyProductsClient(this.apiClient);
  }

  @Memoize()
  get companyProductsStore(): CompanyProductsStore {
    return new CompanyProductsStore(this.companyStore, this.companyProductsClient, this.analyticsManager);
  }

  @Memoize()
  get complianceClient(): ComplianceClient {
    return new ComplianceClient(this.apiClient);
  }

  @Memoize()
  get deepLinkClient(): DeepLinkClient {
    return new DeepLinkClient(this.apiClient);
  }

  @Memoize()
  get directorAddressStore(): DirectorAddressStore {
    return new DirectorAddressStore(this.addressClient);
  }

  @Memoize()
  get directorPositionsStore(): DirectorPositionStore {
    return new DirectorPositionStore(this.dataStorage);
  }

  @Memoize()
  get directorStore(): DirectorStore {
    return new DirectorStore(
      new DirectorClient(this.apiClient),
      this.companyStore,
      this.directorAddressStore,
      this.directorPositionsStore,
      this.config,
    );
  }

  @Memoize()
  get incorporationStore(): IncorporationStore {
    return new IncorporationStore(new IncorporationClient(this.apiClient), this.companyStore);
  }

  @Memoize()
  get leadClient(): LeadClient {
    return new LeadClient(this.apiClient);
  }

  @Memoize()
  get nameCheckerStore(): NameCheckerStore {
    return new NameCheckerStore(new NameCheckerClient(this.apiClient));
  }

  @Memoize()
  get nationalitiesStore(): NationalitiesStore {
    return new NationalitiesStore(new NationalitiesClient(this.apiClient));
  }

  @Memoize()
  get paymentMethodsStore(): PaymentMethodsStore {
    return new PaymentMethodsStore(this.stripeIntegrationClient, this.authenticator, this.analyticsManager);
  }

  @Memoize()
  get stripeIntegrationClient(): StripeIntegrationClient {
    return new StripeIntegrationClient(this.apiClient);
  }

  @Memoize()
  get setupIntentStore(): SetupIntentStore {
    return new SetupIntentStore(this.stripeIntegrationClient, this.analyticsManager, this.config);
  }

  @Memoize()
  get sumsubClient(): SumsubClient {
    return new SumsubClient(this.apiClient);
  }

  @Memoize()
  get sumsubApplicationStore(): SumsubApplicationStore {
    return new SumsubApplicationStore(this.sumsubClient, this.analyticsManager);
  }

  @Memoize()
  get formationExpressStore(): FormationExpressStore {
    return new FormationExpressStore(this.companyProductsClient, this.companyProductsStore);
  }

  @Memoize()
  get billingControllerStore(): BillingControllerStore {
    return this.createBillingControllerStore();
  }

  createBillingControllerStore = (): BillingControllerStore =>
    new BillingControllerStore(new BillingClient(this.apiClient), this.analyticsManager);

  createRefundStore = (): RefundStore => new RefundStore(new BillingClient(this.apiClient), this.analyticsManager);

  @Memoize()
  get stripeFormStore(): StripeFormStore {
    return this.createStripeFormStore();
  }

  createStripeFormStore = (): StripeFormStore => new StripeFormStore(this.setupIntentStore, this.analyticsManager);

  createComplianceStore = (): ComplianceStore =>
    new ComplianceStore(
      this.sumsubApplicationStore,
      this.complianceClient,
      this.addressVerificationStore,
      this.analyticsManager,
    );

  @Memoize()
  get complianceCallClient(): IComplianceCallClient {
    return new ComplianceCallClient(this.apiClient);
  }

  createComplianceCallStore = (): ComplianceCallStore =>
    new ComplianceCallStore(this.complianceCallClient, this.directorStore, this.dataStorage, this.analyticsManager);

  createComplianceWaitStore = (): ComplianceWaitStore =>
    new ComplianceWaitStore(this.complianceClient, this.analyticsManager);

  createDirectorIdentityWidgetStore = (): DirectorIdentityWidgetStore =>
    new DirectorIdentityWidgetStore(
      this.sumsubClient,
      this.featureStore,
      this.analyticsManager,
      this.sumsubApplicationStore,
    );

  @Memoize()
  get directorPoaStore(): DirectorPoaStore {
    return new DirectorPoaStore(
      this.addressClient,
      this.companyStore,
      this.directorStore,
      this.directorAddressStore,
      this.featureStore,
      this.fileShareClient,
      this.analyticsManager,
      this.addressVerificationStore,
      this.config,
    );
  }

  createCompanyNOAnnexStore = (): CompanyNameAnnexNOStore =>
    new CompanyNameAnnexNOStore(this.fileShareClient, this.analyticsManager);

  createAsicAgentSignatureStore = (): AsicAgentSignatureStore =>
    new AsicAgentSignatureStore(this.fileShareClient, this.apiClient, this.analyticsManager);

  createConfirmStepStore = (callback: UpdateFormCallback): ConfirmStepStore =>
    new ConfirmStepStore(callback, this.companyProductsStore, this.formationExpressStore);
}
